import { CATEGORY, ENTERPRISE_PARTNER_ID } from '@brighte/enterprise-common/constants';
import { MaskitoOptions } from '@maskito/core';
import { Heading } from '@spark-web/heading';
import { ClockIcon, InboxInIcon, ThumbUpIcon } from '@spark-web/icon';
import { Inline } from '@spark-web/inline';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import { TextList } from '@spark-web/text-list';
import { SparkThemeName } from '@spark-web/theme';
import { TFunction } from 'i18next';
import { ReactElement } from 'react';
import { Trans } from 'react-i18next';

import { FinancialProductSlugs } from './gql/graphql';
import commbankLogo from './public/commbank-logo.svg';
import commbankMinimalLogo from './public/commbank-minimal-logo.svg';
import electrify2515Logo from './public/electrify-2515-logo-black.svg';
import commbankCoinIcon from './public/icons/commbank/coin.svg';
import racqMembershipIcon from './public/icons/racq/racq-membership.svg';
import racqLogo from './public/racq-logo.svg';
import { MarketplaceChannel, MarketplacePartner, MarketplaceSort } from './types/marketplace';
import { getBrighteSitePartnerUrl } from './utils/formatter';
import { validateRacqMemberId } from './utils/premarketplace/validation/racq';

/** Global configuration */
export const DEFAULT_LOGO_WIDTH = 130;
export const DEFAULT_LOGO_HEIGHT = 40;
export const DEFAULT_LIST_COUNT = 4;
export const DEFAULT_VENDOR_SELECTION_LIMIT = 5;
export const DEFAULT_CATEGORY_ICON_SIZE = 24;

export const RECOMMENDED_VENDORS_LENGTH = 3;

export const BRIGHTE_PHONE_NUMBER = '1300 274 448';

export const ASSET_URL = import.meta.env.VITE_ASSET_URL;

export const RULE_EMAIL_PATTERN = {
  pattern: {
    value: /^[A-Z0-9._%+-]+@[A-Z0-9-]+\.[A-Z0-9-]{2,63}(?:\.[A-Z0-9-]+)*$/i,
    message: 'Email is invalid',
  },
};

export const RULE_MOBILE_NO_PATTERN = {
  pattern: {
    value: /^\d{4} ?\d{3} ?\d{3}$/,
    message: 'Mobile number is invalid',
  },
};

export const RULE_POSTCODE_PATTERN = {
  pattern: {
    value: /\b(0[8-9]\d{2}|[1-7]\d{3})\b/,
    message: 'Postcode is invalid',
  },
};

export const SORT_FIELD_BY_TYPE: Record<
  Exclude<MarketplaceSort, 'DEFAULT'>,
  { type?: string; direction?: 'ASC' | 'DESC'; seed?: string }
> = {
  DISTANCE: {
    type: 'distance',
    direction: 'ASC',
  },
  RANKING: {},
  PRICE_LOW_TO_HIGH: {
    type: 'categoryPrice',
    direction: 'ASC',
  },
  PRICE_HIGH_TO_LOW: {
    type: 'categoryPrice',
    direction: 'DESC',
  },
  RANDOM: {
    seed: 'opportunity-id',
  },
};

/** Supported channels */
export const MARKETPLACE_CHANNELS = ['commbank', 'electrify-2515', 'brighte', 'racq'] as const;

/** Channel & Partner helpers */
export const CHANNEL_TO_FINANCIAL_PRODUCT_ID: Record<MarketplaceChannel, string | undefined> = {
  commbank: 'commbank-residential',
  'electrify-2515': 'electrify-2515',
  brighte: undefined,
  racq: 'brighte-pay',
};

export const CHANNEL_TO_FINANCIAL_PRODUCT_SLUG: Record<MarketplaceChannel, FinancialProductSlugs | undefined> = {
  commbank: FinancialProductSlugs.CommbankResidential,
  'electrify-2515': FinancialProductSlugs.Electrify2515,
  brighte: undefined,
  racq: FinancialProductSlugs.BrightePay,
};

/**
 * A partner may have multiple channels.
 * e.g: rewiring-au (partner) might have electrify-2515, electrify-2000, etc. (channel)
 */
export const CHANNEL_TO_PARTNER = (key?: string | null): MarketplacePartner | undefined => {
  switch (key) {
    case 'commbank':
      return ENTERPRISE_PARTNER_ID.commbank;
    case 'electrify-2515':
      return ENTERPRISE_PARTNER_ID.rewiringAustralia;
    case 'brighte':
      return ENTERPRISE_PARTNER_ID.brighte;
    case 'racq':
      return ENTERPRISE_PARTNER_ID.racq;
    default:
      return undefined;
  }
};

// TODO: this needs rethinking, if a partner has multiple channels
// ET-1051 we can maybe store program in lead data so we can use that instead as channel
// and remove this function
export const PARTNER_TO_CHANNEL = (key: string): MarketplaceChannel | undefined => {
  switch (key) {
    case ENTERPRISE_PARTNER_ID.commbank:
      return 'commbank';
    case ENTERPRISE_PARTNER_ID.rewiringAustralia:
      return 'electrify-2515';
    case ENTERPRISE_PARTNER_ID.brighte:
      return 'brighte';
    case ENTERPRISE_PARTNER_ID.racq:
      return 'racq';
  }
};

/** Channel configuration */
type ChannelConfiguration = {
  // whether a channel has a specific postcode
  fixedPostcode: string | null;
  // map of partner's logos
  logo: {
    default: string | null;
    minimal?: string | null;
  };
  // width of the logo
  logoWidth?: number;
  // whether to show "Provided By" text with the Brighte logo
  showProvidedBy: boolean;
  // whether to show vendor promos in vendor cards
  title?: string;
  // whether to show title in marketplace
  showTitleInMarketplace?: boolean;
  // link to the partner website
  link?: (env: string, partner: string, domain: string) => string;
  // support details for the channel
  supportDetails?: () => ReactElement;
  // categories required for the channel
  requiredCategories?: CATEGORY[];
  // should validate by required categories
  validateByRequiredCategories?: boolean;
  // whether to allow selection of multiple categories
  multiCategorySelection?: boolean;
};

export const CHANNEL_REQUIREMENTS: Record<MarketplaceChannel, ChannelConfiguration> = {
  commbank: {
    fixedPostcode: null,
    logo: {
      default: commbankLogo,
      minimal: commbankMinimalLogo,
    },
    showProvidedBy: true,
    title: 'Home Energy Upgrades',
    showTitleInMarketplace: false,
    link: (env, partner, domain) => `https://${env}${partner}${domain}`,
    multiCategorySelection: true,
  },
  'electrify-2515': {
    fixedPostcode: '2515',
    logo: {
      default: electrify2515Logo,
    },
    showProvidedBy: false,
    title: 'Electrify 2515 Community Pilot',
    link: (env, _, domain) => `https://${env}${domain}/electrify-2515`,
    supportDetails: () => (
      <Stack gap="large">
        <Inline gap="xsmall">
          <Text size="xsmall">If you have any questions</Text>
          <Text size="xsmall">
            <TextLink href="https://www.electrify2515.org/contact" target="_blank">
              contact the Electrify 2515 Pilot Team.
            </TextLink>
          </Text>
        </Inline>
      </Stack>
    ),
    requiredCategories: [CATEGORY.SmartEnergyDevice],
    validateByRequiredCategories: true,
    multiCategorySelection: true,
  },
  brighte: {
    fixedPostcode: null,
    logo: {
      default: null,
    },
    showProvidedBy: false,
    link: (env, _, domain) => `https://${env}${domain}/marketplace`,
    multiCategorySelection: true,
  },
  racq: {
    fixedPostcode: null,
    logo: {
      default: racqLogo,
    },
    logoWidth: 90,
    showProvidedBy: false,
    link: undefined,
    title: 'Home Upgrade Program',
    multiCategorySelection: false,
  },
};

export const CHANNEL_VENDOR_INFO: Partial<Record<MarketplaceChannel, (t: TFunction) => ReactElement>> = {
  'electrify-2515': (t) => (
    <Text size="xsmall" data-testid="electrify-2515-info">
      <Trans
        t={t}
        i18nKey="Electrify 2515 Community Pilot subsidy available—<findOutLink>find out more</findOutLink>"
        components={{
          findOutLink: <TextLink tone="info" target="_blank" href="https://www.electrify2515.org/pilot" />,
        }}
      />
    </Text>
  ),
};

type MarketplaceChannelSubsidyInfo = {
  [category: string]: {
    maxSubsidy?: number;
    customText?: (onSeeDetailsClick?: () => void) => ReactElement;
    details?: () => ReactElement;
  };
};

export const CHANNEL_SUBSIDY_INFO: Partial<Record<MarketplaceChannel, MarketplaceChannelSubsidyInfo>> = {
  // derived from finance/services/core/src/models/quotes/utils/rebate-calculator/e2515.values.ts
  'electrify-2515': {
    [CATEGORY.SmartEnergyDevice]: {
      maxSubsidy: 2500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.SmartEnergyDevice}` }}>
          <Text>
            A fully subsidised smart energy device, valued at around $1,500, installed in every home to track and
            optimise energy use.
          </Text>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.HeatPumpWaterHeater]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.HeatPumpWaterHeater}` }}>
          <Stack gap="xlarge">
            <Text>
              Subsidy of up to $1,000 to replace a gas hot water system (covering up to 50% of the installation cost).
            </Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.ElectricResistanceHotWaterSystem]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.ElectricResistanceHotWaterSystem}` }}>
          <Stack gap="xlarge">
            <Text>
              Subsidy of up to $1,000 to replace a gas hot water system (covering up to 50% of the installation cost).
            </Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.HeatPumpSpaceHeater]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.HeatPumpSpaceHeater}` }}>
          <Stack gap="xlarge">
            <Text>
              Subsidy of up to $1,000 to replace a gas hot water system (covering up to 50% of the installation cost).
            </Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.InductionCooktop]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.InductionCooktop}` }}>
          <Stack gap="xlarge">
            <Text>Subsidy of up to $1,000 to replace gas cooktop (covering up to 50% of the installation cost).</Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank" rel="noopener noreferrer">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.SwitchboardUpgrade]: {
      maxSubsidy: 3000,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.SwitchboardUpgrade}` }}>
          <Text>
            Subsidies are available for upgrades to switchboards where these are required to meet today&apos;s safety
            standards.
          </Text>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
    [CATEGORY.ElectricHeatingCoolingSystemsReverseCycle]: {
      maxSubsidy: 1500,
      details: () => (
        <Stack gap="xxlarge" data={{ testId: `subsidy-info-${CATEGORY.ElectricHeatingCoolingSystemsReverseCycle}` }}>
          <Stack gap="xlarge">
            <Text>Subsidy of up to $1,000 to replace a gas heater (covering up to 50% of the installation cost).</Text>
            <Text>Or up to $1,500 depending on household income (covering up to 70% of the installation cost).</Text>
          </Stack>
          <Text>
            <TextLink href="https://www.electrify2515.org/subsidy-calculator" target="_blank">
              Find out more
            </TextLink>
          </Text>
        </Stack>
      ),
    },
  },
};

/** Partner configuration */
type PartnerConfiguration = {
  requireLead: boolean;
  allowLeadCreation: boolean;
  // whether a vendor can cover some categories or must cover all categories
  multiVendor: boolean;
  // whether partner allows leads to go through the intake flow
  intake: boolean;
  // whether only show vendors that have not reached their leads limit
  premiumLeads: boolean;
  // whether partner has customer ID field in pre-marketplace
  hasCustomerIdField?: 'required' | 'optional' | false;
  // whether partner supports opt-in for marketing communications
  hasMarketingOptIn: boolean;
  // whether partner supports selecting vendors in embedded pre-marketplace
  // if false, suggested vendors will be automatically selected
  premarketplaceSelectVendors: boolean;
  // allow ability for leads to redirect to marketplace proper pick own vendors in pre-marketplace
  pickVendorsInMarketplace: boolean;
  name: string;
  // is marketplace enabled or no for this partner
  marketplaceEnabled: boolean;
  // whether to filter vendors by partnerId in marketplace query
  filterVendorByPartner: boolean;
  // vendors sorting preference
  vendorSortingPreference?: MarketplaceSort;
  // bundle selection
  withBundleSelection: boolean;
  // category options -- if not specified, all categories are available
  categoryOptions?: CATEGORY[];
  // custom asset path
  customAssetPath?: string;
};

type CustomerIdFieldConfig = {
  label?: string;
  description?: string;
  placeholder?: string;
  icon?: () => ReactElement;
  validation?: (value: string) => Promise<boolean | null>;
  maskitoOptions?: MaskitoOptions;
};

// TODO: unify with @constants?
export const PARTNER_REQUIREMENTS: Record<MarketplacePartner, PartnerConfiguration> = {
  commbank: {
    requireLead: true,
    allowLeadCreation: true,
    multiVendor: false,
    intake: false,
    premiumLeads: true,
    hasMarketingOptIn: false,
    name: 'CommBank',
    marketplaceEnabled: true,
    filterVendorByPartner: false,
    withBundleSelection: false,
    premarketplaceSelectVendors: false,
    pickVendorsInMarketplace: true,
  },
  'rewiring-au': {
    requireLead: true,
    allowLeadCreation: false,
    multiVendor: true,
    intake: true,
    premiumLeads: false,
    hasMarketingOptIn: false,
    name: 'Rewiring Australia',
    marketplaceEnabled: true,
    filterVendorByPartner: false,
    withBundleSelection: false,
    premarketplaceSelectVendors: false,
    pickVendorsInMarketplace: false,
  },
  racq: {
    requireLead: false,
    allowLeadCreation: true,
    multiVendor: false,
    intake: false,
    premiumLeads: true,
    hasCustomerIdField: 'optional',
    hasMarketingOptIn: true,
    name: 'RACQ',
    marketplaceEnabled: false,
    filterVendorByPartner: true,
    vendorSortingPreference: 'RANDOM',
    withBundleSelection: true,
    categoryOptions: [CATEGORY.SolarBatteryEvCharger, CATEGORY.HotWaterHeatPumpsHwhp, CATEGORY.WaterFiltration],
    customAssetPath: 'racq',
    premarketplaceSelectVendors: true,
    pickVendorsInMarketplace: false,
  },
  brighte: {
    requireLead: false,
    allowLeadCreation: true,
    multiVendor: false,
    intake: false,
    premiumLeads: false,
    hasMarketingOptIn: false,
    name: 'Brighte',
    marketplaceEnabled: true,
    filterVendorByPartner: false,
    withBundleSelection: false,
    premarketplaceSelectVendors: false,
    pickVendorsInMarketplace: true,
  },
};

export const PARTNER_CUSTOMER_ID_FIELD_CONFIG: Partial<Record<MarketplacePartner, CustomerIdFieldConfig>> = {
  racq: {
    label: 'RACQ membership number',
    description: 'This is your 16-digit membership number',
    placeholder: '0000 0000 0000 0000',
    icon: () => <img src={racqMembershipIcon} alt="RACQ membership icon" />,
    validation: (value?: string) => validateRacqMemberId(value),
    maskitoOptions: {
      mask: [
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ],
    },
  },
};

export const PARTNER_WEBSITE = (key?: string | null) => {
  switch (key) {
    case ENTERPRISE_PARTNER_ID.commbank:
      return 'https://www.commbank.com.au';
    case ENTERPRISE_PARTNER_ID.rewiringAustralia:
      return 'https://www.electrify2515.org/pilot';
    default:
      return 'https://brighte.com.au';
  }
};

export const PARTNER_THEME: Record<string, SparkThemeName> = {
  [ENTERPRISE_PARTNER_ID.commbank]: 'pantheon',
  [ENTERPRISE_PARTNER_ID.rewiringAustralia]: 'brighte',
  [ENTERPRISE_PARTNER_ID.racq]: 'bravo',
};

export const PARTNER_DISCLAIMER: Partial<
  Record<MarketplacePartner, (t: TFunction) => { title: string; content: ReactElement }>
> = {
  // TODO: get from higraph based from partner (ET-587)
  // things to consider: how do we tackle links? and how do we know which word to embed, is it gonna be returned as pure html (dangerous)?
  [ENTERPRISE_PARTNER_ID.commbank]: (t) => ({
    title: 'Things you should know',
    content: (
      <Stack gap="xxlarge">
        <Text size="xsmall">
          {
            "Information contained on this page is only a guide and doesn't consider your individual objectives, financial situation or needs. Before basing any decisions on this information, please consider:"
          }
        </Text>
        <TextList size="xsmall">
          <Text>its appropriateness to your circumstances</Text>
          <Text>
            obtaining professional advice specific to your needs, including financial, taxation and legal advice.
          </Text>
        </TextList>
        <Stack gap="medium">
          <Text size="xsmall" weight="semibold">
            Brighte
          </Text>
          <Text size="xsmall">
            {`'Brighte' is Brighte Capital Pty Limited (ABN 74 609 165 906). Brighte hosts and manages this platform and provides underlying infrastructure to support the administration of the platform.
        Brighte selects the vendors that may be presented on this platform and facilitates the interactions with vendors on this platform, including listing the products, quoting and installations services provided by the vendors.`}
          </Text>
        </Stack>
        <Stack gap="medium">
          <Text size="xsmall" weight="semibold">
            CommBank
          </Text>
          <Text size="xsmall">
            {`'CommBank' refers to Commonwealth Bank of Australia ABN 48 123 123 124. Australian credit licence 234945. The
        platform is for CommBank customers to help them with their home electrification needs. Home energy upgrade
        suggestions, products, vendor referrals and quotes on this platform and installation services are not conducted
        or provided by CommBank.`}
          </Text>
        </Stack>
        <Text size="xsmall">
          CommBank is not authorised and does not make any assertions or recommendations about reduced consumption from
          non-renewable energy sources through energy-efficient product installations. Advice and further information
          should be sought from relevant certified industry professionals.
        </Text>
        <Text size="xsmall">
          {
            "CommBank receives a fee from Brighte in connection with finance provided by CommBank for a home energy package purchased via Brighte's Marketplace."
          }
        </Text>
        <Text size="xsmall">
          <Trans
            t={t}
            i18nKey="The target market for these CommBank financial products will be found within the product's Target Market Determination, <tmdLink>available here.</tmdLink>"
            components={{
              tmdLink: (
                <TextLink
                  tone="info"
                  target="_blank"
                  href={`${getBrighteSitePartnerUrl(ENTERPRISE_PARTNER_ID.commbank)}/tmd`}
                />
              ),
            }}
          />
        </Text>
        <Text size="xsmall">
          <Trans
            t={t}
            i18nKey="Applications for finance are subject to CommBank's normal credit approval. View the <personalLoanTermsLink>Personal Loan Terms and Conditions</personalLoanTermsLink> and <instalPayTermsLink>InstalPay Terms and Conditions</instalPayTermsLink>. Full terms and conditions will be included in our loan offer. Fees and charges payable."
            components={{
              personalLoanTermsLink: (
                <TextLink
                  tone="info"
                  target="_blank"
                  href={`${getBrighteSitePartnerUrl(ENTERPRISE_PARTNER_ID.commbank)}/personal-loan-terms`}
                />
              ),
              instalPayTermsLink: (
                <TextLink
                  tone="info"
                  target="_blank"
                  href={`${getBrighteSitePartnerUrl(ENTERPRISE_PARTNER_ID.commbank)}/instalpay-terms`}
                />
              ),
            }}
          />
        </Text>
      </Stack>
    ),
  }),
};

export const PARTNER_BRIGHTE_DISCLAIMER: Partial<Record<MarketplacePartner, ReactElement>> = {
  [ENTERPRISE_PARTNER_ID.commbank]: (
    <Text size="xsmall">
      {
        "Brighte is not part of the CBA Group. CommBank and its related entities do not sell, issue or guarantee the obligations or performance of Brighte nor the products and services that Brighte's vendors offer. The relationship with Brighte does not represent a deposit with or liability of either CommBank or any of its related bodies corporate."
      }
    </Text>
  ),
};

export const PARTNER_SUCCESS_INFO = (partner: MarketplacePartner) => {
  switch (partner) {
    default:
      return [
        {
          title: (
            <Text size="xsmall" weight="semibold">
              Vendors will be in touch
            </Text>
          ),
          description: (
            <Text size="xsmall">
              Once vendors receive your request, they will contact you in the next couple of days to discuss your
              request and book a site visit.
            </Text>
          ),
          icon: <ClockIcon />,
        },
        {
          title: (
            <Text size="xsmall" weight="semibold">
              Review and accept quotes online
            </Text>
          ),
          description: (
            <Text size="xsmall">
              Brighte will email you when your quote(s) is ready to review. You can select the quote package that best
              fits your needs and budget.
            </Text>
          ),
          icon: <InboxInIcon />,
        },
        {
          title: (
            <Text size="xsmall" weight="semibold">
              Installation
            </Text>
          ),
          description: (
            <Text size="xsmall">
              Once your application for finance has been approved, or you have arranged payment with your chosen vendor,
              they will schedule the installation with you.
            </Text>
          ),
          icon: <ThumbUpIcon />,
        },
      ];
  }
};

export const PARTNER_ACTION_CARD: Partial<
  Record<
    MarketplacePartner,
    {
      icon: string;
      title: ReactElement;
      description: ReactElement;
    }
  >
> = {
  [ENTERPRISE_PARTNER_ID.commbank]: {
    icon: commbankCoinIcon,
    title: (
      <Heading level="3" as="h2">
        Need Financing?
      </Heading>
    ),
    description: (
      <Stack gap="xlarge">
        <Text size="xsmall">
          CommBank has financing options that you can explore to cover the costs of your home energy upgrades. These
          options will be presented to you by CommBank once you accept your quote and select how you would like to pay.
        </Text>
        <Text size="xsmall">
          To find out more about CommBank financing offers, visit commbank.com.au/homeenergyfinance
        </Text>
      </Stack>
    ),
  },
};

export const PARTNER_ASSET_PATH = (partner?: MarketplacePartner) => {
  const customAssetPath = partner ? PARTNER_REQUIREMENTS[partner]?.customAssetPath : undefined;
  return customAssetPath ? `${ASSET_URL}/${customAssetPath}` : ASSET_URL;
};

export const PARTNER_WITH_PRICING_DISPLAY: MarketplacePartner[] = [
  ENTERPRISE_PARTNER_ID.commbank,
  ENTERPRISE_PARTNER_ID.rewiringAustralia,
  ENTERPRISE_PARTNER_ID.brighte,
];

export const PARTNER_BRIGHTE_MODAL_FOOTER: Partial<Record<MarketplacePartner, string>> = {
  [ENTERPRISE_PARTNER_ID.commbank]: 'Provided by',
};

export const BUNDLE_SUBCATEGORIES: Partial<Record<CATEGORY, CATEGORY[]>> = {
  [CATEGORY.SolarBatteryEvCharger]: [CATEGORY.SolarSystem, CATEGORY.BatteryStorage, CATEGORY.EvCharger],
  [CATEGORY.SolarBatterySystem]: [CATEGORY.SolarSystem, CATEGORY.BatteryStorage],
  [CATEGORY.SolarEvCharger]: [CATEGORY.SolarSystem, CATEGORY.EvCharger],
  [CATEGORY.BatteryEvCharger]: [CATEGORY.BatteryStorage, CATEGORY.EvCharger],
};
